<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="flex flex-col h-full bg-white dark:bg-slate-900 w-full max-w-lg mx-auto max-h-[80vh]">
      <!-- Header -->
      <div class="px-6 py-4 border-b border-n-weak flex justify-between items-center">
        <h2 class="text-lg font-semibold m-0 text-slate-800 dark:text-slate-100">
          {{ viewMode === 'list' ? 'Produtos / Serviços' : (isEditing ? 'Editar Produto' : 'Novo Produto') }}
        </h2>
        <button
          class="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 border-none bg-transparent cursor-pointer"
          @click="onClose"
        >
          <span class="i-lucide-x w-5 h-5 text-slate-500"></span>
        </button>
      </div>

      <!-- List View -->
      <div v-if="viewMode === 'list'" class="flex-1 overflow-y-auto">
        <div v-if="products.length === 0" class="flex flex-col items-center justify-center py-12 text-n-slate-10">
          <span class="text-4xl mb-3">📦</span>
          <p class="text-sm m-0">Nenhum produto cadastrado</p>
        </div>

        <div v-else class="divide-y divide-n-weak">
          <div
            v-for="product in products"
            :key="product.id"
            class="flex items-center justify-between px-6 py-3 hover:bg-n-alpha-1"
          >
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <span class="text-sm font-semibold text-n-slate-12">{{ product.name }}</span>
                <span v-if="product.category" class="text-xs bg-n-alpha-2 text-n-slate-11 px-1.5 py-0.5 rounded">{{ product.category }}</span>
              </div>
              <div class="flex items-center gap-2 mt-0.5">
                <span class="text-sm font-bold text-emerald-500">{{ formatPrice(product.price) }}</span>
                <span v-if="product.description" class="text-xs text-n-slate-10 truncate max-w-[200px]">· {{ product.description }}</span>
              </div>
            </div>
            <div class="flex gap-2">
              <button
                class="button size--small"
                @click="openEditForm(product)"
              >
                Editar
              </button>
              <button
                class="button size--small color--danger"
                @click="deleteProduct(product.id)"
              > 
                Excluir
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Form View -->
      <div v-else class="flex-1 overflow-y-auto px-6 py-6 space-y-4">
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nome *</label>
          <input
            v-model="form.name"
            type="text"
            placeholder="Ex: Plano Mensal"
            class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Preço (R$) *</label>
          <input
            v-model.number="form.price"
            type="number"
            step="0.01"
            min="0"
            placeholder="0,00"
            class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Categoria</label>
          <input
            v-model="form.category"
            type="text"
            placeholder="Ex: Assinatura, Serviço, Produto"
            class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Descrição</label>
          <textarea
            v-model="form.description"
            rows="3"
            placeholder="Descrição do produto ou serviço..."
            class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm resize-none focus:ring-2 focus:ring-emerald-500"
          ></textarea>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-n-weak flex items-center justify-between">
        <div>
          <button
            v-if="viewMode === 'form'"
            class="px-3 py-2 text-sm font-medium rounded-md border border-n-weak bg-transparent text-n-slate-11 hover:bg-n-alpha-1 cursor-pointer"
            @click="viewMode = 'list'"
          >
            ← Voltar
          </button>
        </div>
        <button
          v-if="viewMode === 'list'"
          class="button success"
          @click="openNewForm"
        >
          + Novo Produto
        </button>
        <button
          v-else
          class="button success"
          :disabled="!isFormValid"
          @click="saveProduct"
        >
          {{ isEditing ? 'Atualizar' : 'Criar Produto' }}
        </button>
      </div>
    </div>
  </woot-modal>
</template>

<script>
export default {
  name: 'ProductsManager',
  props: {
    show: { type: Boolean, default: false },
    products: { type: Array, default: () => [] },
  },
  emits: ['close', 'update-products'],
  data() {
    return {
      viewMode: 'list',
      isEditing: false,
      editingProductId: null,
      form: { name: '', price: 0, category: '', description: '' },
    };
  },
  computed: {
    isFormValid() {
      return this.form.name.trim() && this.form.price > 0;
    },
  },
  methods: {
    formatPrice(val) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
    },
    openNewForm() {
      this.isEditing = false;
      this.editingProductId = null;
      this.form = { name: '', price: 0, category: '', description: '' };
      this.viewMode = 'form';
    },
    openEditForm(product) {
      this.isEditing = true;
      this.editingProductId = product.id;
      this.form = {
        name: product.name,
        price: product.price,
        category: product.category || '',
        description: product.description || '',
      };
      this.viewMode = 'form';
    },
    saveProduct() {
      if (!this.isFormValid) return;

      let updated = [...this.products];
      const data = {
        name: this.form.name.trim(),
        price: parseFloat(this.form.price) || 0,
        category: this.form.category.trim(),
        description: this.form.description.trim(),
      };

      if (this.isEditing) {
        const idx = updated.findIndex(p => p.id === this.editingProductId);
        if (idx >= 0) updated[idx] = { ...updated[idx], ...data };
      } else {
        updated.push({ id: `p_${Date.now()}`, ...data });
      }

      this.$emit('update-products', updated);
      this.viewMode = 'list';
    },
    deleteProduct(id) {
      if (!confirm('Excluir este produto?')) return;
      const updated = this.products.filter(p => p.id !== id);
      this.$emit('update-products', updated);
    },
    onClose() {
      this.viewMode = 'list';
      this.$emit('close');
    },
  },
};
</script>
